// src/services/api/ambassadors.ts - API клиент для амбассадоров (обновлен под новые маршруты)

import { apiClient, ApiResponse } from './index';
import {
  AmbassadorProfile,
  AmbassadorDetail,
  AmbassadorPeriodReport,
  AmbassadorTransaction,
  ClientAmbassadorLink,
  AmbassadorSettings,
  CreateAmbassadorRequest,
  UpdateAmbassadorRequest,
  AmbassadorFilters,
  AmbassadorProgramStats,
  PromoCodeValidation,
} from '@/types/models/ambassador';

export const ambassadorsApi = {
  // Получить список амбассадоров с фильтрами
  getAmbassadors: (params?: AmbassadorFilters) =>
    apiClient.get<ApiResponse<{
      ambassadors: AmbassadorProfile[];
      total_count: number;
    }>>('/api/admin/ambassadors/', { params }),

  // Получить детальную информацию об амбассадоре
  getAmbassador: (ambassadorId: number) =>
    apiClient.get<ApiResponse<AmbassadorDetail>>(
      `/api/admin/ambassadors/${ambassadorId}/`
    ),

  // Создать нового амбассадора
  createAmbassador: (data: CreateAmbassadorRequest) =>
    apiClient.post<ApiResponse<AmbassadorProfile>>(
      '/api/admin/ambassadors/',
      data
    ),

  // Обновить данные амбассадора
  updateAmbassador: (ambassadorId: number, data: UpdateAmbassadorRequest) =>
    apiClient.patch<ApiResponse<AmbassadorProfile>>(
      `/api/admin/ambassadors/${ambassadorId}/`,
      data
    ),

  // Удалить амбассадора (мягкое удаление - деактивация)
  deleteAmbassador: (ambassadorId: number) =>
    apiClient.delete<ApiResponse<{ message: string }>>(
      `/api/admin/ambassadors/${ambassadorId}/`
    ),

  // Активировать амбассадора
  activateAmbassador: (ambassadorId: number) =>
    apiClient.post<ApiResponse<{ message: string }>>(
      `/api/admin/ambassadors/${ambassadorId}/`,
      {},
      { params: { action: 'activate' } }
    ),

  // Деактивировать амбассадора
  deactivateAmbassador: (ambassadorId: number) =>
    apiClient.post<ApiResponse<{ message: string }>>(
      `/api/admin/ambassadors/${ambassadorId}/`,
      {},
      { params: { action: 'deactivate' } }
    ),

  // Получить отчеты амбассадора за периоды
  getAmbassadorReports: (ambassadorId: number, params?: {
    date_from?: string;
    date_to?: string;
    limit?: number;
    offset?: number;
  }) =>
    apiClient.get<ApiResponse<{
      reports: AmbassadorPeriodReport[];
      total_count: number;
    }>>(`/api/admin/ambassadors/${ambassadorId}/`, { 
      params: { ...params, action: 'reports' }
    }),

  // Получить транзакции амбассадора
  getAmbassadorTransactions: (ambassadorId: number, params?: {
    transaction_type?: string;
    date_from?: string;
    date_to?: string;
    limit?: number;
    offset?: number;
  }) =>
    apiClient.get<ApiResponse<{
      transactions: AmbassadorTransaction[];
      pagination: {
        total: number;
        limit: number;
        offset: number;
        has_next: boolean;
      };
    }>>(`/api/admin/ambassadors/${ambassadorId}/`, { 
      params: { ...params, action: 'transactions' }
    }),

  // Получить привлеченных клиентов амбассадора
  getAmbassadorClients: (ambassadorId: number, params?: {
    limit?: number;
    offset?: number;
    date_from?: string;
    date_to?: string;
  }) =>
    apiClient.get<ApiResponse<{
      clients: ClientAmbassadorLink[];
      total_count: number;
    }>>(`/api/admin/ambassadors/${ambassadorId}/`, { 
      params: { ...params, action: 'clients' }
    }),

  // Добавить/списать бонусы вручную (adjustment)
  adjustAmbassadorBalance: (ambassadorId: number, data: {
    amount: number;
    description: string;
  }) =>
    apiClient.post<ApiResponse<{
      new_balance: number;
      adjustment_amount: number;
    }>>(`/api/admin/ambassadors/${ambassadorId}/`, data, {
      params: { action: 'adjust_balance' }
    }),

  // Получить общую статистику программы
  getProgramStats: (params?: {
    date_from?: string;
    date_to?: string;
  }) =>
    apiClient.get<ApiResponse<AmbassadorProgramStats>>(
      '/api/admin/ambassadors/bulk/',
      { params: { ...params, operation: 'stats' } }
    ),

  // Проверить валидность промокода (через клиентский API, если есть)
  validatePromoCode: (promoCode: string) =>
    apiClient.post<ApiResponse<PromoCodeValidation>>(
      '/api/client/validate-promo/',
      { promo_code: promoCode }
    ),

  // Получить настройки программы
  getSettings: () =>
    apiClient.get<ApiResponse<AmbassadorSettings>>(
      '/api/admin/ambassadors/settings/'
    ),

  // Обновить настройки программы
  updateSettings: (data: Partial<AmbassadorSettings>) =>
    apiClient.patch<ApiResponse<AmbassadorSettings>>(
      '/api/admin/ambassadors/settings/',
      data
    ),

  // Экспортировать данные амбассадоров в CSV
  exportAmbassadors: (params?: AmbassadorFilters) =>
    apiClient.get('/api/admin/ambassadors/bulk/', {
      params: { ...params, operation: 'export' },
      responseType: 'blob',
    }),

  // Массовая активация/деактивация
  bulkUpdateStatus: (data: {
    ambassador_ids: number[];
    is_active: boolean;
  }) =>
    apiClient.post<ApiResponse<{
      updated_count: number;
    }>>('/api/admin/ambassadors/bulk/', data, {
      params: { operation: 'bulk_update' }
    }),

  // Запустить расчет периода вручную (для конкретного амбассадора)
  calculatePeriod: (ambassadorId: number, data: {
    period_start: string;
    period_end: string;
  }) =>
    apiClient.post<ApiResponse<any>>(
      `/api/admin/ambassadors/${ambassadorId}/`,
      data,
      { params: { action: 'calculate_period' } }
    ),

  // Получить список клиентов для выбора (для создания амбассадора)
  getAvailableCustomers: (params?: {
    search?: string;
    page?: number;
    per_page?: number;
  }) =>
    apiClient.get<ApiResponse<{
      count: number;
      results: Array<{
        id: number;
        name: string;
        phone: string;
        email?: string;
        is_ambassador: boolean;
      }>;
    }>>('/api/admin/customers/', { params }),
};